import { formatDollars } from "@payrollmart-invoice/shared";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";

interface LowestMarginCustomer {
	customerId: string;
	customerName: string;
	revenue: number;
	margin: number;
	invoiceCount: number;
}

interface InsightCardProps {
	/** The lowest-margin customer, if any, surfaced from dashboardSummary. */
	lowestMargin: LowestMarginCustomer | null;
	mtdRevenueCents: number;
	ytdRevenueCents: number;
	monthLabel: string;
	yearLabel: string;
}

/**
 * "Worth noticing" narrative card. Generates copy from the data rather than
 * hardcoding it — per handoff §4 "Do not add the 'Worth Noticing' copy as
 * static text. Generate it from data with a small helper." The lowest-
 * margin customer (if any) gets a deep link so Paul can act on the insight.
 */
export function InsightCard({
	lowestMargin,
	mtdRevenueCents,
	ytdRevenueCents,
	monthLabel,
	yearLabel,
}: InsightCardProps) {
	const marginPct =
		lowestMargin && lowestMargin.revenue > 0
			? Math.round((lowestMargin.margin / lowestMargin.revenue) * 100)
			: null;

	return (
		<div
			className="rounded-lg border border-[#e5e7eb] bg-gradient-to-br from-white to-[#f8f9fb] p-6"
			data-testid="insight-card"
		>
			<div className="flex items-center gap-2">
				<Sparkles className="h-4 w-4 text-[#4f46e5]" strokeWidth={1.5} />
				<h2 className="text-sm font-semibold text-[#1e1e20]">Worth noticing</h2>
			</div>
			<p className="mt-4 text-[17px] font-semibold leading-snug tracking-tight text-[#1e1e20]">
				{ytdRevenueCents > 0
					? `${yearLabel} is at ${formatDollars(ytdRevenueCents)} billed year-to-date.`
					: `No invoices billed yet in ${yearLabel}.`}{" "}
				{mtdRevenueCents > 0
					? `${monthLabel} has posted ${formatDollars(mtdRevenueCents)} so far.`
					: ""}
			</p>
			{lowestMargin && marginPct !== null && marginPct < 30 && (
				<>
					<p className="mt-4 text-[13px] leading-relaxed text-[#6b7280]">
						<span className="font-medium text-[#1e1e20]">{lowestMargin.customerName}</span> is your
						thinnest margin at <strong className="text-[#d97706]">{marginPct}%</strong>
						{lowestMargin.invoiceCount >= 1
							? ` across ${lowestMargin.invoiceCount} ${lowestMargin.invoiceCount === 1 ? "invoice" : "invoices"}`
							: ""}
						. Worth a conversation before the next cycle.
					</p>
					<Link
						to="/customers/$customerId"
						params={{ customerId: lowestMargin.customerId }}
						className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-[#e5e7eb] bg-white px-3 py-1.5 text-xs font-medium text-[#1e1e20] transition-colors hover:border-[#4f46e5] hover:text-[#4f46e5]"
					>
						Open {lowestMargin.customerName}
						<ArrowRight className="h-3 w-3" strokeWidth={2} />
					</Link>
				</>
			)}
			{(!lowestMargin || marginPct === null || marginPct >= 30) && (
				<p className="mt-4 text-[13px] leading-relaxed text-[#6b7280]">
					Every customer is pulling weight on margin. Nothing urgent to look at.
				</p>
			)}
		</div>
	);
}
